// "Konsolide (grup müşteri)" seçiminin ipucu (2026-10-08 manuel test: seçenekler ekranda
// okunabilmeli). İki katman: seçili kuralın tek satır açıklaması + ⓘ düğmesi — üzerine gelince /
// odaklanınca tüm kuralları örnekleriyle listeleyen açılır kutu (tıklayınca sabit kalır).
// İçerik tek kaynak: shared KPI_AGGREGATION_HELP (kılavuz tablosu da aynı metinden).

import { useId, useState } from "react";
import {
  KPI_AGGREGATIONS,
  KPI_AGGREGATION_HELP,
  KPI_AGGREGATION_LABELS,
  type KpiAggregation,
} from "@mahir/shared";

export interface AggregationHintProps {
  /** seçili kural; set penceresinde "katalog varsayılanı" iken varsayılanın kendisi */
  value: KpiAggregation;
  /** hesaplanan (formüllü) gösterge — "Yeniden hesaplanır" yalnız burada anlamlı */
  isDerived: boolean;
  /** set penceresinde "Katalog varsayılanı (…)" seçiliyken true — açıklamaya önek eklenir */
  inherited?: boolean;
}

export function describeAggregation(value: KpiAggregation, isDerived: boolean): string {
  const h = KPI_AGGREGATION_HELP[value];
  if (value === "recompute" && !isDerived) {
    return `${h.summary} Bu göstergede formül yok; işletme değerleri toplanır.`;
  }
  if (value === "sum" && isDerived) {
    return `${h.summary} Formül bölme içeriyorsa işletme sonuçlarını toplamak yanlış sayı verir — oran için "Yeniden hesaplanır" seçin.`;
  }
  return h.caveat ? `${h.summary} ${h.caveat}` : h.summary;
}

export function AggregationHint({
  value,
  isDerived,
  inherited = false,
}: AggregationHintProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const [pinned, setPinned] = useState(false);
  const popId = useId();
  const show = open || pinned;
  return (
    <div style={{ position: "relative" }} data-testid="kpi-aggregation-hint">
      <div className="text-xs flex items-start gap-1" style={{ color: "var(--color-text-3)" }}>
        <span style={{ flex: 1, minWidth: 0 }}>
          {inherited ? "Katalog varsayılanı · " : ""}
          <strong style={{ fontWeight: 600 }}>{KPI_AGGREGATION_LABELS[value]}:</strong>{" "}
          {describeAggregation(value, isDerived)}
        </span>
        <button
          type="button"
          aria-label="Konsolide kurallarını açıkla"
          aria-expanded={show}
          aria-controls={popId}
          title="Tüm kurallar ve örnekler"
          onMouseEnter={() => setOpen(true)}
          onMouseLeave={() => setOpen(false)}
          onFocus={() => setOpen(true)}
          onBlur={() => setOpen(false)}
          onClick={() => setPinned((p) => !p)}
          style={{
            flex: "0 0 auto",
            width: 22,
            height: 22,
            borderRadius: 11,
            border: "1px solid var(--color-border)",
            background: pinned ? "var(--color-dark)" : "var(--color-surface)",
            color: "var(--color-primary)",
            fontSize: "0.75rem",
            fontWeight: 700,
            lineHeight: 1,
            cursor: "pointer",
          }}
        >
          i
        </button>
      </div>
      {show ? (
        <div
          id={popId}
          role="tooltip"
          onMouseEnter={() => setOpen(true)}
          onMouseLeave={() => setOpen(false)}
          style={{
            position: "absolute",
            right: 0,
            top: "100%",
            zIndex: 30,
            marginTop: 4,
            width: "min(440px, 92vw)",
            padding: 12,
            borderRadius: 10,
            border: "1px solid var(--color-border)",
            background: "var(--color-surface)",
            boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
            display: "grid",
            gap: 10,
          }}
        >
          <div className="text-xs" style={{ color: "var(--color-text-3)" }}>
            Grup müşteride işletme değerlerinden grup satırı nasıl türetilir? Örnekler: Üretim
            1.200.000 + Lojistik 300.000 kWh, çalışan 240 + 60. Tek işletmeli müşteride etkisi yok.
          </div>
          {KPI_AGGREGATIONS.map((a) => {
            const h = KPI_AGGREGATION_HELP[a];
            const active = a === value;
            return (
              <div
                key={a}
                style={{
                  display: "grid",
                  gap: 2,
                  padding: "6px 8px",
                  borderRadius: 8,
                  background: active ? "var(--color-dark)" : "transparent",
                }}
              >
                <div className="text-sm" style={{ color: "var(--color-text)", fontWeight: 600 }}>
                  {KPI_AGGREGATION_LABELS[a]}
                  {h.forDerivedOnly ? (
                    <span
                      className="text-xs"
                      style={{ color: "var(--color-text-3)", fontWeight: 400 }}
                    >
                      {" "}
                      · yalnız hesaplanan (ƒx)
                    </span>
                  ) : null}
                </div>
                <div className="text-xs" style={{ color: "var(--color-text-2)" }}>
                  {h.summary}
                </div>
                <div className="text-xs" style={{ color: "var(--color-text-3)" }}>
                  Örnek: <span style={{ fontVariantNumeric: "tabular-nums" }}>{h.example}</span> ·
                  Uygun: {h.bestFor}
                </div>
                {h.caveat ? (
                  <div className="text-xs" style={{ color: "var(--color-warning-text)" }}>
                    {h.caveat}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
